import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { UseQueryResult } from "@tanstack/react-query";
import { useElectricitySavings } from "@hooks/queries/use-electricity-savings.ts";
import {
  HalfHourlyConsumptionTariffs,
  SavingsResponse,
} from "@givenergy/sales-simple-energy-comparison";
import { useDebounce } from "use-debounce";
import {
  isFullGridKwh,
  isFullHalfHourlyTariff,
  OptionalHalfHourlyTariff,
} from "@lib/types.ts";
import { useLocalStorage } from "usehooks-ts";
import { v4 as uuid } from "uuid";
import { buildHalfHourlyTariffFromPoints } from "@lib/tariff.ts";
import { ContactInformationData } from "@components/forms/contact-information/types.ts";
import { useHouseholdInformationStore } from "@stores/household-information-store.ts";
import { useShallow } from "zustand/react/shallow";
import { useLastYearsTariffStore } from "@stores/tariff/last-years-tariff-store.ts";
import { useComparisonTariffStore } from "@stores/tariff/comparison-tariff-store.ts";
import { useCreateLead } from "@hooks/mutations/use-create-lead.ts";
import { useSystemConfiguration } from "@stores/system-configuration-store.ts";
import { useSolarConfigurationStore } from "@stores/solar-configuration-store.ts";
import { useHasSolar } from "@hooks/use-has-solar";

type CustomTariff = {
  standingCharge: number;
  importTariff: HalfHourlyConsumptionTariffs;
};

export type HouseholdInformationContext = {
  simpleSavingsQuery: UseQueryResult<SavingsResponse>;
  createLead: (contactInformation: ContactInformationData) => Promise<void>;
  lastYearsTariff: CustomTariff | undefined;
  lastYearsHalfHourlyTariff: OptionalHalfHourlyTariff;
  comparisonTariff: CustomTariff | undefined;
  comparisonHalfHourlyTariff: OptionalHalfHourlyTariff;
  userId: string;
};

const HouseholdInformationContext =
  createContext<HouseholdInformationContext | null>(null);

export const useHouseholdInformation = () => {
  const ctx = useContext(HouseholdInformationContext);
  if (!ctx) {
    throw new Error(
      "HouseholdInformationContext must be used within the provider",
    );
  }

  return ctx;
};

type HouseholdInformationProviderProps = PropsWithChildren;

export const HouseholdInformationProvider = ({
  children,
}: HouseholdInformationProviderProps) => {
  // Keeping track of the user using a non-identifying user id persisted to the device.
  // In the future we might omit this and just identify users by full address.
  const [userId] = useLocalStorage("givenergy:sales-app:session-id", uuid());

  const householdInformation = useHouseholdInformationStore(
    useShallow((state) => ({
      hasEv: state.hasEv,
      houseType: state.houseType,
      heatingType: state.heatingType,
      meterType: state.meterType,
      postcode: state.postcode,
      annualConsumption: state.annualConsumption,
      gridKwh: state.gridKwh,
      isValid: state.isValid,
    })),
  );

  const useLastYearsTariff = useLastYearsTariffStore(
    (state) => state.useTariff,
  );
  const lastYearsTariffPoints = useLastYearsTariffStore(
    (state) => state.tariffPoints,
  );
  const lastYearsStandingCharge = useLastYearsTariffStore(
    (state) => state.standingCharge,
  );

  const useComparisonTariff = useComparisonTariffStore(
    (state) => state.useTariff,
  );
  const comparisonTariffPoints = useComparisonTariffStore(
    (state) => state.tariffPoints,
  );
  const comparisonStandingCharge = useComparisonTariffStore(
    (state) => state.standingCharge,
  );
  const comparisonExportRate = useComparisonTariffStore(
    (state) => state.exportRate,
  );

  const systemConfiguration = useSystemConfiguration(
    useShallow((state) => ({
      batteryCapacity: state.batteryCapacity,
    })),
  );

  const hasSolar = useHasSolar();
  const solarConfiguration = useSolarConfigurationStore(
    useShallow((state) => ({
      hasSolar: state.hasSolar,
      solarArraySize: state.solarArraySize,
    })),
  );

  const { mutateAsync: createLeadMutation } = useCreateLead();

  const lastYearsHalfHourlyTariff = useMemo(() => {
    return buildHalfHourlyTariffFromPoints(lastYearsTariffPoints);
  }, [lastYearsTariffPoints]);

  const comparisonHalfHourlyTariff = useMemo(() => {
    return buildHalfHourlyTariffFromPoints(comparisonTariffPoints);
  }, [comparisonTariffPoints]);

  const [debouncedHouseholdInformation] = useDebounce(
    householdInformation,
    500,
  );

  const [debouncedSystemConfiguration] = useDebounce(systemConfiguration, 500);
  const [debouncedSolarConfiguration] = useDebounce(solarConfiguration, 500);

  const lastYearsTariff = useMemo(() => {
    if (lastYearsStandingCharge === null) {
      return undefined;
    }

    if (!isFullHalfHourlyTariff(lastYearsHalfHourlyTariff)) {
      return undefined;
    }

    return {
      standingCharge: lastYearsStandingCharge,
      importTariff: lastYearsHalfHourlyTariff,
    };
  }, [lastYearsStandingCharge, lastYearsHalfHourlyTariff]);

  const comparisonTariff = useMemo(() => {
    if (comparisonStandingCharge === null) {
      return undefined;
    }

    if (!isFullHalfHourlyTariff(comparisonHalfHourlyTariff)) {
      return undefined;
    }

    return {
      standingCharge: comparisonStandingCharge,
      exportRate: comparisonExportRate ?? undefined,
      importTariff: comparisonHalfHourlyTariff,
    };
  }, [
    comparisonStandingCharge,
    comparisonExportRate,
    comparisonHalfHourlyTariff,
  ]);

  const electricitySavingsQuery = useElectricitySavings(
    userId,
    hasSolar,
    {
      householdInformation: {
        ...debouncedHouseholdInformation,
        annualConsumptionKwh:
          debouncedHouseholdInformation.annualConsumption ?? undefined,
        gridKwh: isFullGridKwh(debouncedHouseholdInformation.gridKwh)
          ? debouncedHouseholdInformation.gridKwh
          : undefined,
      },
      lastYearsTariff: useLastYearsTariff ? lastYearsTariff : undefined,
      comparisonTariff: useComparisonTariff ? comparisonTariff : undefined,
      systemConfiguration: debouncedSystemConfiguration,
      solarConfiguration: debouncedSolarConfiguration,
    },
    householdInformation.isValid,
  );

  const createLead = useCallback(
    async (contactInformation: ContactInformationData) => {
      await createLeadMutation({
        userId,
        contactInformation,
        leadInformation: {
          ...householdInformation,
          annualConsumptionKwh:
            electricitySavingsQuery.data?.meta.annualConsumptionKwh ?? 0,
        },
        systemConfiguration,
      });

      window.gtag("event", "lead_created", {
        userId,
        ...householdInformation,
      });
    },
    [householdInformation, electricitySavingsQuery.data],
  );

  useEffect(() => {
    window.hj("identify", userId, { userId });
  }, [userId]);

  const value = useMemo(
    () => ({
      simpleSavingsQuery: electricitySavingsQuery,
      createLead,
      lastYearsTariff,
      lastYearsHalfHourlyTariff,
      comparisonTariff,
      comparisonHalfHourlyTariff,
      userId,
    }),
    [
      electricitySavingsQuery,
      createLead,
      lastYearsTariff,
      lastYearsHalfHourlyTariff,
      comparisonTariff,
      comparisonHalfHourlyTariff,
      userId,
    ],
  );

  return (
    <HouseholdInformationContext.Provider value={value}>
      {children}
    </HouseholdInformationContext.Provider>
  );
};
